import { type ComponentProps } from 'react'
import { Field as BaseField } from '@base-ui/react/field'
import { cva, type VariantProps } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'
import { FieldLabel } from './nested/label'
import { FieldNote } from './nested/note'
import { FieldGroup, useFieldGroupContext } from './nested/group'
import { FieldContext, useFieldContext, useFieldRequired } from './nested/context'
import { FieldItem } from './nested/item'
import { LEADING_LAYOUT } from './nested/styles'
import type { PartProps } from '../../lib/part-props'

/**
 * Four shapes, and no two of them are one row read from a different end.
 *
 * vertical    label above the control, note below it. A form column.
 * horizontal  label in a fixed lane, control fills, note under the control. A form row. The
 *             control is the elastic part because what goes in it is unknown; the label is a
 *             known short phrase.
 * justified   label fills, control sized to itself at the far end, note under the label. A
 *             settings row. Here the control is the fixed part, because a select or a toggle is
 *             one size whatever it is attached to, and the sentence naming the setting is
 *             the elastic part.
 * leading     control first, label and note beside it. A checkbox or radio, where the control is
 *             a mark on the label's first line rather than a box of its own.
 *
 * The note always sits under whichever side is elastic, since a note wraps and belongs in the
 * column with room. Where it sits under the label it is 2px away, the text gap; under a control it
 * is 6px, the field gap.
 *
 * The label lane is a fixed number, not `max-content`. A lane that sizes to its own content steps
 * the left edge of every row in a list, and a column of controls that do not line up is worse than
 * a lane occasionally too wide for one word. Override it on the form, never on a single field.
 */
const fieldVariants = cva('group/field w-full', {
  variants: {
    orientation: {
      vertical: 'flex flex-col gap-1.5',
      horizontal: [
        'grid grid-cols-[120px_minmax(0,1fr)] grid-rows-[minmax(28px,auto)] items-center gap-x-4 gap-y-1.5',
        '[&>[data-slot=field-label]]:col-start-1 [&>*:not([data-slot=field-label])]:col-start-2',
        // A hidden label gives up its lane, so the control takes the full row.
        '[&:has(>[data-sr-only])]:grid-cols-[minmax(0,1fr)] [&:has(>[data-sr-only])]:gap-x-0',
        '[&:has(>[data-sr-only])>*]:!col-start-1',
      ].join(' '),
      justified: [
        'grid grid-cols-[minmax(0,1fr)_max-content] grid-rows-[minmax(28px,auto)] items-center gap-x-4 gap-y-0.5',
        '[&>[data-slot=field-label]]:col-start-1 [&>[data-slot=field-note]]:col-start-1',
        // Row 1 whatever order the children come in, so the note can follow the label in the markup.
        '[&>*:not([data-slot=field-label]):not([data-slot=field-note])]:col-start-2 [&>*:not([data-slot=field-label]):not([data-slot=field-note])]:row-start-1 [&>*:not([data-slot=field-label]):not([data-slot=field-note])]:justify-self-end',
        // The label and the control are centred on each other, so in a 28px row an 18px label
        // floats in 5px of slack. The note sits directly under the label, so that slack would land
        // between a label and its own note. Pulling it back by half the difference keeps the
        // painted distance at the text gap.
        '[&>[data-slot=field-note]]:-mt-[5px]',
      ].join(' '),
      leading: LEADING_LAYOUT,
    },
  },
  defaultVariants: {
    orientation: 'vertical',
  },
})

type FieldOrientation = NonNullable<VariantProps<typeof fieldVariants>['orientation']>

interface FieldProps extends PartProps<ComponentProps<typeof BaseField.Root>>, VariantProps<typeof fieldVariants> {
  className?: string
  /** Marks the control required and prints the asterisk on the label, from one flag. */
  required?: boolean
}

/**
 * Lays a label, a control and a note out together, and wires them to each other.
 *
 * Built on Base UI `Field`, so the label's `htmlFor`, the note's `aria-describedby` and the
 * control's `aria-invalid` are generated rather than hand-passed. `Input`, `Select.Trigger`,
 * `Checkbox` and the rest pick those up by sitting inside it.
 */
function Field({ className, orientation, required = false, children, ...props }: FieldProps) {
  const group = useFieldGroupContext()
  return (
    <FieldContext.Provider value={{ required }}>
      <BaseField.Root
        data-slot="field"
        className={twMerge(fieldVariants({ orientation: orientation ?? group?.orientation }), className)}
        {...props}
      >
        {children}
      </BaseField.Root>
    </FieldContext.Provider>
  )
}

Field.Label = FieldLabel
Field.Note = FieldNote
Field.Group = FieldGroup
Field.Item = FieldItem
/** Base UI's own parts, for the cases the wrapper does not cover. */
Field.Control = BaseField.Control
Field.Validity = BaseField.Validity

export { Field, fieldVariants, useFieldContext, useFieldRequired }
export type { FieldProps, FieldOrientation }
